import { useState, type FormEvent } from "react";
import { FaWhatsapp } from "react-icons/fa6";
import FileUploadField from "./FileUploadField";
import HoneypotField from "./HoneypotField";
import EnquiryConfirmation from "./EnquiryConfirmation";
import useEnquiryForm from "../hooks/useEnquiryForm";
import { submitSupplierEnquiry } from "../utils/supplierEnquiry";
import { getWhatsAppLink, SUPPLIER_ENQUIRY_MESSAGE } from "../data/whatsappContext";
import type { SeafoodCategory } from "../data/seafood";

const BUSINESS_TYPES = ["Individual Fisherman", "Fishing Co-operative", "Aquaculture Farm", "Trading Business"];
const CATCH_TYPES: (SeafoodCategory | "Mixed")[] = ["Fish", "Shellfish", "Premium", "Mixed"];

export default function SupplierEnquiryForm() {
  const { status, referenceNumber, submit, reset } = useEnquiryForm("supplier");
  const [honeypot, setHoneypot] = useState("");

  const [businessType, setBusinessType] = useState("");
  const [location, setLocation] = useState("");
  const [catchType, setCatchType] = useState("");
  const [estimatedVolume, setEstimatedVolume] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [gstDoc, setGstDoc] = useState<string | null>(null);
  const [fssaiDoc, setFssaiDoc] = useState<string | null>(null);
  const [licenceDoc, setLicenceDoc] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (honeypot) return; // silently drop — never reveal the bot check to whatever filled it in
    submit(() =>
      submitSupplierEnquiry({
        businessType,
        location,
        catchType,
        estimatedVolume,
        contactName,
        contactEmail,
        gstDocumentName: gstDoc ?? undefined,
        fssaiDocumentName: fssaiDoc ?? undefined,
        licenceDocumentName: licenceDoc ?? undefined,
      })
    );
  };

  if (status === "success" && referenceNumber) {
    return <EnquiryConfirmation type="supplier" referenceNumber={referenceNumber} onSendAnother={reset} />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <HoneypotField value={honeypot} onChange={setHoneypot} />

      <div className="grid gap-5 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Business / entity type</span>
          <select
            required
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          >
            <option value="" disabled>
              Select one
            </option>
            {BUSINESS_TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Location (city/harbour)</span>
          <input
            required
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Species / catch type</span>
          <select
            required
            value={catchType}
            onChange={(e) => setCatchType(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          >
            <option value="" disabled>
              Select one
            </option>
            {CATCH_TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Estimated supply volume</span>
          <input
            required
            type="text"
            placeholder="e.g. 200 kg/week"
            value={estimatedVolume}
            onChange={(e) => setEstimatedVolume(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Contact name</span>
          <input
            required
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Email</span>
          <input
            required
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          />
        </label>
      </div>

      <div className="space-y-4 rounded-xl bg-foam-50 p-4 dark:bg-white/5">
        <p className="text-xs font-semibold uppercase tracking-wide text-ocean-800/60 dark:text-foam-100/60">
          Compliance documents (PDF or image, max 10MB each)
        </p>
        <FileUploadField label="GST registration (if applicable)" accept="application/pdf,image/*" maxSizeMB={10} onFileSelected={setGstDoc} />
        <FileUploadField label="FSSAI registration" accept="application/pdf,image/*" maxSizeMB={10} onFileSelected={setFssaiDoc} />
        <FileUploadField label="Boat / fishing licence" accept="application/pdf,image/*" maxSizeMB={10} onFileSelected={setLicenceDoc} />
      </div>

      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full rounded-full bg-teal-400 py-3 text-sm font-semibold text-ocean-950 transition-colors hover:bg-teal-300 disabled:opacity-50"
      >
        {status === "loading" ? "Submitting…" : "Submit Application"}
      </button>
      {status === "error" && <p className="text-center text-xs text-coral-500">Something went wrong — please try again.</p>}

      <p className="text-center text-xs text-ocean-800/50 dark:text-foam-100/50">
        Prefer not to fill out a form?{" "}
        <a
          href={getWhatsAppLink(SUPPLIER_ENQUIRY_MESSAGE)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-semibold text-teal-600 hover:text-teal-700"
        >
          <FaWhatsapp className="h-3.5 w-3.5" />
          Reach us on WhatsApp instead
        </a>
      </p>
    </form>
  );
}
